import { NotificationContext } from '@app/models/enums'
import { Notification } from '@app/models/Notification'
import {
  INotificationsContextState,
  NotificationsContextState,
} from '@app/models/NotificationsContextState'
import { ApplicationState } from '@app/store'
import {
  ActionTypes as AccountActionTypes,
  ISetLoggedInUserAction,
} from '@app/store/modules/account'
import { errorToast } from '@app/utils/notifications'
import * as NotificationsSocket from '@app/utils/notificationsSocket'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { LOCATION_CHANGE } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface ISystemNotificationsState {
  readonly Calendar: INotificationsContextState
  readonly Standard: INotificationsContextState
}

const SystemNotificationsStateRecord = Record({
  Calendar: new NotificationsContextState(),
  Standard: new NotificationsContextState(),
})

export class SystemNotificationsState extends SystemNotificationsStateRecord
  implements ISystemNotificationsState {
  public static fromJS(initialState?: Partial<ISystemNotificationsState>) {
    if (!initialState) {
      return new SystemNotificationsState()
    }

    const { Calendar, Standard, ...rest } = initialState

    return new SystemNotificationsState({
      Calendar: NotificationsContextState.fromJS(Calendar),
      Standard: NotificationsContextState.fromJS(Standard),
      ...rest,
    })
  }

  public readonly Calendar: INotificationsContextState
  public readonly Standard: INotificationsContextState

  constructor(props?: Partial<SystemNotificationsState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadNotifications = 'SYSTEM_NOTIFICATIONS::LOAD_NOTIFICATIONS',
  LoadNotificationsFailure = 'SYSTEM_NOTIFICATIONS::LOAD_NOTIFICATIONS_FAILURE',
  LoadNotificationsRequest = 'SYSTEM_NOTIFICATIONS::LOAD_NOTIFICATIONS_REQUEST',
  LoadNotificationsSuccess = 'SYSTEM_NOTIFICATIONS::LOAD_NOTIFICATIONS_SUCCESS',
  MarkAllSeen = 'SYSTEM_NOTIFICATIONS::MARK_ALL_SEEN',
  MarkAllSeenFailure = 'SYSTEM_NOTIFICATIONS::MARK_ALL_SEEN_FAILURE',
  MarkAllSeenRequest = 'SYSTEM_NOTIFICATIONS::MARK_ALL_SEEN_REQUEST',
  MarkAllSeenSuccess = 'SYSTEM_NOTIFICATIONS::MARK_ALL_SEEN_SUCCESS',
  MarkSeen = 'SYSTEM_NOTIFICATIONS::MARK_SEEN',
  MarkSeenFailure = 'SYSTEM_NOTIFICATIONS::MARK_SEEN_FAILURE',
  MarkSeenRequest = 'SYSTEM_NOTIFICATIONS::MARK_SEEN_REQUEST',
  MarkSeenSuccess = 'SYSTEM_NOTIFICATIONS::MARK_SEEN_SUCCESS',
  ResetState = 'SYSTEM_NOTIFICATIONS::RESET_STATE',
  SetUnseenCount = 'SYSTEM_NOTIFICATIONS::SET_UNSEEN_COUNT',
  ToggleOpen = 'SYSTEM_NOTIFICATIONS::TOGGLE_OPEN',
}

interface ILoadNotificationsAction {
  context: NotificationContext
  type: ActionTypes.LoadNotifications
}

interface ILoadNotificationsFailureAction {
  context: NotificationContext
  error: string
  type: ActionTypes.LoadNotificationsFailure
}

interface ILoadNotificationsRequestAction {
  context: NotificationContext
  type: ActionTypes.LoadNotificationsRequest
}

interface ILoadNotificationsSuccessAction {
  context: NotificationContext
  items: List<Notification>
  type: ActionTypes.LoadNotificationsSuccess
  unseenCount: number
}

interface IMarkAllSeenAction {
  context: NotificationContext
  type: ActionTypes.MarkAllSeen
}

interface IMarkAllSeenFailureAction {
  context: NotificationContext
  error: string
  type: ActionTypes.MarkAllSeenFailure
}

interface IMarkAllSeenRequestAction {
  context: NotificationContext
  type: ActionTypes.MarkAllSeenRequest
}

interface IMarkAllSeenSuccessAction {
  context: NotificationContext
  type: ActionTypes.MarkAllSeenSuccess
}

interface IMarkSeenAction {
  context: NotificationContext
  notificationId: string
  type: ActionTypes.MarkSeen
}

interface IMarkSeenFailureAction {
  context: NotificationContext
  error: string
  type: ActionTypes.MarkSeenFailure
}

interface IMarkSeenSuccessAction {
  context: NotificationContext
  notification: Notification
  type: ActionTypes.MarkSeenSuccess
}

interface IResetStateAction {
  context: NotificationContext
  type: ActionTypes.ResetState
}

interface ISetUnseenCountAction {
  context: string
  type: ActionTypes.SetUnseenCount
  unseenCount: number
}

interface IToggleOpenAction {
  context: NotificationContext
  open: boolean
  type: ActionTypes.ToggleOpen
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadNotificationsFailureAction
  | ILoadNotificationsRequestAction
  | ILoadNotificationsSuccessAction
  | IMarkAllSeenFailureAction
  | IMarkAllSeenRequestAction
  | IMarkAllSeenSuccessAction
  | IMarkSeenFailureAction
  | IMarkSeenSuccessAction
  | NotificationsSocket.INewNotificationReceivedAction
  | IResetStateAction
  | ISetUnseenCountAction
  | IToggleOpenAction
  | NotificationsSocket.IUpdateNotificationReceivedAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadNotifications(
  context: NotificationContext,
): ILoadNotificationsAction {
  return {
    context,
    type: ActionTypes.LoadNotifications,
  }
}

function loadNotificationsFailure(
  context: NotificationContext,
  error: string,
): ILoadNotificationsFailureAction {
  return {
    context,
    error,
    type: ActionTypes.LoadNotificationsFailure,
  }
}

function loadNotificationsRequest(
  context: NotificationContext,
): ILoadNotificationsRequestAction {
  return {
    context,
    type: ActionTypes.LoadNotificationsRequest,
  }
}

function loadNotificationsSuccess(
  context: NotificationContext,
  items: List<Notification>,
  unseenCount: number,
): ILoadNotificationsSuccessAction {
  return {
    context,
    items,
    type: ActionTypes.LoadNotificationsSuccess,
    unseenCount,
  }
}

function markAllSeen(context: NotificationContext): IMarkAllSeenAction {
  return {
    context,
    type: ActionTypes.MarkAllSeen,
  }
}

function markAllSeenFailure(
  context: NotificationContext,
  error: string,
): IMarkAllSeenFailureAction {
  return {
    context,
    error,
    type: ActionTypes.MarkAllSeenFailure,
  }
}

function markAllSeenRequest(
  context: NotificationContext,
): IMarkAllSeenRequestAction {
  return {
    context,
    type: ActionTypes.MarkAllSeenRequest,
  }
}

function markAllSeenSuccess(
  context: NotificationContext,
): IMarkAllSeenSuccessAction {
  return {
    context,
    type: ActionTypes.MarkAllSeenSuccess,
  }
}

function markSeen(
  context: NotificationContext,
  notificationId: string,
): IMarkSeenAction {
  return {
    context,
    notificationId,
    type: ActionTypes.MarkSeen,
  }
}

function markSeenFailure(
  context: NotificationContext,
  error: string,
): IMarkSeenFailureAction {
  return {
    context,
    error,
    type: ActionTypes.MarkSeenFailure,
  }
}

function markSeenSuccess(
  context: NotificationContext,
  notification: Notification,
): IMarkSeenSuccessAction {
  return {
    context,
    notification,
    type: ActionTypes.MarkSeenSuccess,
  }
}

function resetState(context: NotificationContext): IResetStateAction {
  return {
    context,
    type: ActionTypes.ResetState,
  }
}

function setUnseenCount(
  context: NotificationContext,
  unseenCount: number,
): ISetUnseenCountAction {
  return {
    context,
    type: ActionTypes.SetUnseenCount,
    unseenCount,
  }
}

function toggleOpen(
  context: NotificationContext,
  open: boolean,
): IToggleOpenAction {
  return {
    context,
    open,
    type: ActionTypes.ToggleOpen,
  }
}

export const actionCreators = {
  loadNotifications,
  markAllSeen,
  resetState,
  toggleOpen,
}

export const notificationItemActionCreators = {
  markSeen,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new SystemNotificationsState()

export const reducer: Reducer<SystemNotificationsState> = (
  state: SystemNotificationsState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadNotificationsFailure:
      return state
        .setIn([action.context, 'error'], action.error)
        .setIn([action.context, 'isLoading'], false) as SystemNotificationsState

    case ActionTypes.LoadNotificationsRequest:
      return state.setIn(
        [action.context, 'isLoading'],
        true,
      ) as SystemNotificationsState

    case ActionTypes.LoadNotificationsSuccess:
      return state
        .setIn([action.context, 'error'], undefined)
        .setIn([action.context, 'isLoading'], false)
        .setIn([action.context, 'items'], action.items)
        .setIn(
          [action.context, 'unseenCount'],
          action.unseenCount,
        ) as SystemNotificationsState

    case ActionTypes.MarkAllSeenFailure:
      return state
        .setIn([action.context, 'error'], action.error)
        .setIn(
          [action.context, 'isReadingAll'],
          false,
        ) as SystemNotificationsState

    case ActionTypes.MarkAllSeenRequest:
      return state.setIn(
        [action.context, 'isReadingAll'],
        true,
      ) as SystemNotificationsState

    case ActionTypes.MarkAllSeenSuccess: {
      const context = state.get(action.context) as NotificationsContextState

      if (context) {
        const newItems = context.items.map((item) => {
          if (item === undefined) {
            return item
          }
          return item.set('seen', true)
        })
        return state
          .setIn([action.context, 'isReadingAll'], false)
          .setIn([action.context, 'items'], newItems)
          .setIn([action.context, 'unseenCount'], 0) as SystemNotificationsState
      } else {
        return state
      }
    }

    case ActionTypes.MarkSeenFailure:
      return state.setIn(
        [action.context, 'error'],
        action.error,
      ) as SystemNotificationsState

    case ActionTypes.MarkSeenSuccess: {
      const context = state.get(action.context) as NotificationsContextState

      if (context) {
        const itemIndex = context.items.findIndex(
          (n) => n !== undefined && n.id === action.notification.id,
        )
        if (itemIndex > -1) {
          return state
            .setIn([action.context, 'items', itemIndex], action.notification)
            .setIn([action.context, 'unseenCount'], context.unseenCount - 1) as SystemNotificationsState
        }
      }

      return state
    }

    case NotificationsSocket.ActionTypes.NewNotificationReceived: {
      const context = state.get(action.context) as NotificationsContextState

      if (context) {
        const itemIndex = context.items.findIndex(
          (n) => n !== undefined && n.id === action.notification.id,
        )

        if (itemIndex > -1) {
          return state.setIn(
            [action.context, 'items'],
            context.items.delete(itemIndex).insert(0, action.notification),
          ) as SystemNotificationsState
        } else {
          return state.setIn(
            [action.context, 'items'],
            context.items.insert(0, action.notification),
          ) as SystemNotificationsState
        }
      }

      return state
    }

    case ActionTypes.ResetState:
      return state.set(
        action.context,
        new NotificationsContextState(),
      ) as SystemNotificationsState

    case ActionTypes.SetUnseenCount:
      return state.setIn(
        [action.context, 'unseenCount'],
        action.unseenCount,
      ) as SystemNotificationsState

    case ActionTypes.ToggleOpen:
      return state.setIn(
        [action.context, 'isOpen'],
        action.open,
      ) as SystemNotificationsState

    case NotificationsSocket.ActionTypes.UpdateNotificationReceived: {
      const context = state.get(action.context) as NotificationsContextState

      if (context) {
        const itemIndex = context.items.findIndex(
          (n) => n !== undefined && n.id === action.notification.id,
        )

        const newItems: List<Notification> =
          itemIndex > -1
            ? context.items.set(itemIndex, action.notification)
            : context.items.insert(0, action.notification)

        const sortedItems = newItems.sortBy((item) =>
          item !== undefined ? -item.dateCreated : undefined,
        ) as List<Notification>

        return state.setIn(
          [action.context, 'items'],
          sortedItems,
        ) as SystemNotificationsState
      }

      return state
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  if (incomingAction.type === LOCATION_CHANGE) {
    return state
      .setIn([NotificationContext.Calendar, 'isOpen'], false)
      .setIn(
        [NotificationContext.Standard, 'isOpen'],
        false,
      ) as SystemNotificationsState
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadNotificationsSaga(action: ILoadNotificationsAction) {
  try {
    yield put(loadNotificationsRequest(action.context))

    const reqUrl = `/api/Notifications/My?count=20&context=${action.context}`

    const result = yield makeAuthenticatedRequest(reqUrl)

    if (result.success === true) {
      const items: List<Notification> = List(
        result.results.map((i: any) => Notification.fromJS(i)),
      )
      yield put(
        loadNotificationsSuccess(action.context, items, result.count || 0),
      )
    } else {
      yield put(loadNotificationsFailure(action.context, result.message))
    }
  } catch (err) {
    yield put(loadNotificationsFailure(action.context, err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* markAllSeenSaga(action: IMarkAllSeenAction) {
  try {
    yield put(markAllSeenRequest(action.context))
    const reqUrl = `/api/Notifications/UpdateAll/${action.context}`
    const result = yield makeAuthenticatedRequest(reqUrl, {
      method: RequestMethod.POST,
    })

    if (result.success === true) {
      yield put(markAllSeenSuccess(action.context))
    } else {
      yield put(markAllSeenFailure(action.context, result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(markAllSeenFailure(action.context, err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* markSeenSaga(action: IMarkSeenAction) {
  try {
    const reqUrl = `/api/Notifications/Update/${action.notificationId}`
    const result = yield makeAuthenticatedRequest(reqUrl, {
      method: RequestMethod.POST,
    })

    if (result.success === true) {
      yield put(
        markSeenSuccess(action.context, Notification.fromJS(result.entity)),
      )
    } else {
      yield put(markSeenFailure(action.context, result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(markSeenFailure(action.context, err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* setLoggedInUserSaga(action: ISetLoggedInUserAction) {
  try {
    NotificationsSocket.disconnect()

    NotificationsSocket.connect(action.accessToken)

    yield put(loadNotifications(NotificationContext.Standard))
    yield put(loadNotifications(NotificationContext.Calendar))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* updateUnseenCountSaga(
  action:
    | NotificationsSocket.INewNotificationReceivedAction
    | NotificationsSocket.IUpdateNotificationReceivedAction,
) {
  try {
    const isReadingAll: boolean = yield select(
      selectIsReadingAll(action.context),
    )

    if (!isReadingAll) {
      const result = yield makeAuthenticatedRequest(
        `/api/Notifications/My/Unseen?context=${action.context}`,
      )

      if (typeof result === 'number') {
        yield put(setUnseenCount(action.context, result))
      }
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadNotifications() {
  yield takeEvery(ActionTypes.LoadNotifications, loadNotificationsSaga)
}

function* watchMarkAllSeen() {
  yield takeEvery(ActionTypes.MarkAllSeen, markAllSeenSaga)
}

function* watchMarkSeen() {
  yield takeEvery(ActionTypes.MarkSeen, markSeenSaga)
}

function* watchSetLoggedInUser() {
  yield takeLatest(AccountActionTypes.SetLoggedInUser, setLoggedInUserSaga)
}

function* watchUpdateUnseenCount() {
  const triggers = [
    NotificationsSocket.ActionTypes.NewNotificationReceived,
    NotificationsSocket.ActionTypes.UpdateNotificationReceived,
  ]
  yield takeEvery(triggers, updateUnseenCountSaga)
}

export function* systemNotificationsRootSaga() {
  yield fork(watchLoadNotifications)
  yield fork(watchMarkAllSeen)
  yield fork(watchMarkSeen)
  yield fork(watchSetLoggedInUser)
  yield fork(watchUpdateUnseenCount)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.systemNotifications

export const selectContext = (context: NotificationContext) =>
  createSelector(selectModuleState(), (mState) => {
    const cState = mState.get(context) as NotificationsContextState
    return cState || new NotificationsContextState()
  })

export const selectError = (context: NotificationContext) =>
  createSelector(selectContext(context), (cState) => cState.error)

export const selectIsLoading = (context: NotificationContext) =>
  createSelector(selectContext(context), (cState) => cState.isLoading)

export const selectIsOpen = (context: NotificationContext) =>
  createSelector(selectContext(context), (cState) => cState.isOpen)

export const selectIsReadingAll = (context: NotificationContext) =>
  createSelector(selectContext(context), (cState) => cState.isReadingAll)

export const selectItems = (context: NotificationContext) =>
  createSelector(selectContext(context), (cState) => cState.items)

export const selectUnseenCount = (context: NotificationContext) =>
  createSelector(selectContext(context), (cState) => cState.unseenCount)
